import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { getPrompts, SystemPrompt } from '../../services/promptService';
import { PROMPT_STAGES, PromptStageMeta } from '../../constants/articlePromptStages';
import PromptEditor from './PromptEditor';

type PromptType = 'system' | 'user' | 'block';

/** Every editable prompt of the article pipeline, in pipeline order, each with a
 * short card saying what the stage is for and when it runs. Rule blocks are
 * single texts inserted into several stage prompts through their placeholder. */
const ArticlePromptsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);

  const reload = async () => {
    const rows = await getPrompts('article generation');
    setPrompts(rows ?? []);
  };

  useEffect(() => {
    reload();
  }, []);

  const findPrompt = (subFeature: string, type: PromptType) =>
    prompts.find((p) => p.sub_feature === subFeature && p.type === type && p.is_active);

  const ordered = [...PROMPT_STAGES].sort((a, b) => a.order - b.order);
  const stages = ordered.filter((s) => (s.kind ?? 'stage') === 'stage');
  const blocks = ordered.filter((s) => s.kind === 'block');
  const typesOf = (stage: PromptStageMeta): PromptType[] =>
    stage.kind === 'block' ? ['block'] : ['system', 'user'];

  return (
    <div className="max-w-4xl">
      <ErrorBoundary>
        <div className="space-y-8">
          <section className="border rounded-lg p-5 bg-gray-50" data-testid="prompt-pipeline">
            <h2 className="font-semibold mb-2">{t('articlePrompts.pipeline_title')}</h2>
            <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-0.5">
              {stages.map((stage) => (
                <li key={stage.subFeature}>
                  <span className="font-medium">
                    {t(`articlePrompts.stage_${stage.subFeature}`).replace(/^\d+\s*·\s*/, '')}
                  </span>
                  {': '}
                  {t(`articlePrompts.when_${stage.subFeature}`)}
                </li>
              ))}
            </ol>
            <div className="mt-2 text-xs text-gray-500">{t('articlePrompts.pipeline_note')}</div>
          </section>

          {ordered.map((stage) => (
            <div
              key={stage.subFeature}
              className="border rounded-lg p-5 space-y-4"
              data-testid={`prompt-stage-${stage.subFeature}`}
            >
              <div>
                <h2 className="font-semibold text-lg">
                  {t(`articlePrompts.stage_${stage.subFeature}`)}
                </h2>
                <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                  <dt className="text-gray-500">{t('articlePrompts.goal_label')}</dt>
                  <dd className="text-gray-800">{t(`articlePrompts.goal_${stage.subFeature}`)}</dd>
                  <dt className="text-gray-500">{t('articlePrompts.when_label')}</dt>
                  <dd className="text-gray-800">{t(`articlePrompts.when_${stage.subFeature}`)}</dd>
                </dl>
                <div className="mt-1 text-sm text-gray-600">
                  {t(`articlePrompts.recipient_${stage.recipient}`)}
                </div>
                {stage.jsonContract && (
                  <div className="mt-2 text-xs text-gray-600">
                    {t('articlePrompts.json_contract')}
                    <pre className="mt-1 bg-gray-50 border rounded p-2 overflow-x-auto">
                      {stage.jsonContract}
                    </pre>
                  </div>
                )}
              </div>
              {typesOf(stage).map((type) => {
                const prompt = findPrompt(stage.subFeature, type);
                return prompt ? (
                  <PromptEditor
                    key={`${stage.subFeature}-${type}-${prompt.id}`}
                    stage={stage}
                    type={type}
                    promptId={prompt.id}
                    initialContent={prompt.content}
                    onSaved={reload}
                  />
                ) : (
                  <div key={`${stage.subFeature}-${type}`} className="text-sm text-red-600">
                    {t('articlePrompts.prompt_missing')} ({type})
                  </div>
                );
              })}
            </div>
          ))}
          {blocks.length === 0 && null}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default ArticlePromptsPanel;
